if (!customElements.get('countdown-timer')) {
  customElements.define(
    'countdown-timer',
    class CountdownTimer extends HTMLElement {
      constructor() {
        super();
        this.MS_IN_SEC = 1000;
        this.MS_IN_MIN = 60 * this.MS_IN_SEC;
        this.MS_IN_HRS = 60 * this.MS_IN_MIN;
        this.MS_IN_DAY = 24 * this.MS_IN_HRS;

        this.selectorMapping = {
          day: '[data-timer-day]',
          hour: '[data-timer-hour]',
          minute: '[data-timer-minute]',
          second: '[data-timer-second]',
        };

        this.units = ['day', 'hour', 'minute', 'second'];
        this.type = this.dataset.type || 'evergreen';
        this.duration = this.dataset.duration;
        this.loop = false; // Non-daily timers can use this if needed
        this.shouldAddLeadingZero = true;

        this.initCountdown();
      }

      initCountdown() {
        switch (this.type) {
          case 'fixed_time':
            if (this.dataset.dueDate) {
              this.targetTime = new Date(this.dataset.dueDate).getTime();
            }
            break;

          case 'daily':
            if (this.dataset.daily) {
              const [hours, minutes] = this.dataset.daily.split(':').map(Number);
              this.targetTime = this.getNextDailyTime(hours, minutes).getTime();
            }
            break;

          default: // Evergreen timers
            this.targetTime = this.getEvergreenDueDate().getTime();
            break;
        }

        if (!this.targetTime) return;

        this.startTime = Date.now();
        if (this.targetTime > this.startTime) {
          this.fetchDomElements();
          this.beginCountdown();
        }
      }

      fetchDomElements() {
        this.elements = {};
        this.units.forEach((unit) => {
          this.elements[unit] = this.querySelector(this.selectorMapping[unit]);
        });
      }

      beginCountdown() {
        this.intervalId = setInterval(() => {
          const remainingTime = this.targetTime - Date.now();
          if (remainingTime <= 0) {
            this.terminateCountdown();
          } else {
            this.updateDisplay(remainingTime);
          }
        }, 1000);

        this.classList.remove('hidden');
      }

      updateDisplay(ms) {
        const timeComponents = this.computeTimeComponents(ms);
        this.units.forEach((unit) => {
          if (this.elements[unit]) {
            this.elements[unit].textContent = this.formatTimeComponent(timeComponents[unit]);
          }
        });
      }

      computeTimeComponents(ms) {
        return {
          day: Math.floor(ms / this.MS_IN_DAY),
          hour: Math.floor((ms % this.MS_IN_DAY) / this.MS_IN_HRS),
          minute: Math.floor((ms % this.MS_IN_HRS) / this.MS_IN_MIN),
          second: Math.floor((ms % this.MS_IN_MIN) / this.MS_IN_SEC),
        };
      }

      formatTimeComponent(value) {
        return this.shouldAddLeadingZero && value < 10 ? `0${value}` : value.toString();
      }

      terminateCountdown() {
        clearInterval(this.intervalId);

        if (this.type === 'daily' && this.dataset.daily) {
          // Reset the countdown for the next day
          const [hours, minutes] = this.dataset.daily.split(':').map(Number);
          this.targetTime = this.getNextDailyTime(hours, minutes).getTime();
          this.beginCountdown();
        } else if (this.loop) {
          this.beginCountdown();
        } else {
          this.classList.add('hidden');
        }
      }

      resetCountdown() {
        clearInterval(this.intervalId);
        this.units.forEach((unit) => {
          if (this.elements[unit]) this.elements[unit].textContent = '00';
        });
      }

      getNextDailyTime(hours, minutes) {
        const now = new Date();
        const target = new Date(now);
        target.setHours(hours, minutes, 0, 0);

        // If today's time has passed, set to tomorrow
        if (target <= now) target.setDate(target.getDate() + 1);
        return target;
      }

      getEvergreenDueDate() {
        const now = new Date();
        const dueDate = new Date(now);
        let intervalHrs;

        switch (this.duration) {
          case 'every_month':
            dueDate.setMonth(dueDate.getMonth() + 1);
            dueDate.setDate(1);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;

          case 'every_week':
            const dayOfWeek = now.getDay();
            dueDate.setDate(now.getDate() + (7 - dayOfWeek));
            dueDate.setHours(0, 0, 0, 0);
            return dueDate;

          case 'every_2_hrs': intervalHrs = 2; break;
          case 'every_6_hrs': intervalHrs = 6; break;
          case 'every_12_hrs': intervalHrs = 12; break;
          case 'every_24_hrs': intervalHrs = 24; break;
        }

        if (intervalHrs) {
          const currentHour = now.getHours();
          const startPoints = Array.from({ length: 24 / intervalHrs }, (_, i) => i * intervalHrs);

          for (const hour of startPoints) {
            if (currentHour < hour || (currentHour === hour && now.getMinutes() === 0 && now.getSeconds() === 0)) {
              dueDate.setHours(hour, 0, 0, 0);
              return dueDate;
            }
          }

          // If no point left today, roll over to first point tomorrow
          dueDate.setDate(dueDate.getDate() + 1);
          dueDate.setHours(startPoints[0], 0, 0, 0);
          return dueDate;
        }

        return dueDate;
      }
    }
  );
}
